"use client";

import { GTMItemListTracker } from "@/components/features/analytics/gtm-event-trackers";
import ProductPreview from "@/components/features/shop/product-preview/product-preview";
import { Button } from "@/components/shared/ui/button";
import { GTM } from "@/lib/analytics/analytics-index";
import { GTMItemListID } from "@/lib/analytics/analytics-types";
import { ShopBrowsingData } from "@/lib/shop/shop-types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

export const GRID_CONFIG_SMALL = {
    base: "grid-cols-2",
    sm: "sm:grid-cols-2",
    md: "md:grid-cols-4",
    lg: "lg:grid-cols-4",
    xl: "xl:grid-cols-6",
    "2xl": "2xl:grid-cols-8",
    "3xl": "3xl:grid-cols-8",
    "4xl": "4xl:grid-cols-12"
};

const getColumnsForBreakpoint = (width: number): number => {
    if (width >= 1800) return 12; // 4xl
    if (width >= 1600) return 8; // 3xl
    if (width >= 1536) return 8; // 2xl
    if (width >= 1280) return 6; // xl
    if (width >= 1024) return 4; // lg
    if (width >= 768) return 4; // md
    if (width >= 640) return 2; // sm
    return 2; // base
};

export default function ProductCarousel({
    products,
    GTMItemListID,
    className
}: {
    products: ShopBrowsingData[];
    GTMItemListID: GTMItemListID;
    className?: string;
}) {
    const [currentPage, setCurrentPage] = useState(0);
    const [columns, setColumns] = useState(6);
    const router = useRouter();

    useEffect(() => {
        const updateColumns = () => {
            setColumns(getColumnsForBreakpoint(window.innerWidth));
        };

        updateColumns();
        window.addEventListener("resize", updateColumns);
        return () => window.removeEventListener("resize", updateColumns);
    }, []);

    const { totalPages, currentPageProducts } = useMemo(() => {
        const itemsPerPage = columns;
        const totalPages = Math.ceil(products.length / itemsPerPage);
        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentPageProducts = products.slice(startIndex, endIndex);

        return { totalPages, currentPageProducts };
    }, [products, columns, currentPage]);

    useEffect(() => {
        if (currentPage >= totalPages && totalPages > 0) {
            setCurrentPage(totalPages - 1);
        }
    }, [currentPage, totalPages]);

    const goToPreviousPage = () => {
        GTM.sendEvent({ event: "go_to_previous_page", ecommerce: { item_list_id: GTMItemListID } });

        setCurrentPage((prev) => {
            if (prev === 0) {
                return totalPages - 1; // Wrap to last page
            }
            return prev - 1;
        });
    };

    const goToNextPage = () => {
        GTM.sendEvent({ event: "go_to_next_page", ecommerce: { item_list_id: GTMItemListID } });
        setCurrentPage((prev) => {
            if (prev >= totalPages - 1) {
                return 0;
            }
            return prev + 1;
        });
    };

    const setCurrentPageOverride = (page_number: number) => {
        GTM.sendEvent({ event: "set_current_page", ecommerce: { item_list_id: GTMItemListID } });
        setCurrentPage(page_number);
    };

    if (products.length === 0) {
        return (
            <div className={`w-full ${className}`}>
                <div className="flex h-48 items-center justify-center text-muted-foreground">
                    No products available
                </div>
            </div>
        );
    }

    return (
        <GTMItemListTracker
            itemListID={GTMItemListID}
            page_number={currentPage + 1}
            products={currentPageProducts}
        >
            <div className={`relative w-full ${className} h-fit overflow-hidden`}>
                <div className="flex h-[450px] items-center">
                    <Button
                        variant="outline"
                        size="icon"
                        className="mr-4 h-8 w-8 flex-shrink-0 border-gray-400"
                        onClick={goToPreviousPage}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex-1 overflow-hidden">
                        <div className={`grid gap-2 ${Object.values(GRID_CONFIG_SMALL).join(" ")}`}>
                            {Array.from({ length: columns }, (_, index) => {
                                const product = currentPageProducts[index];
                                if (!product) {
                                    return null;
                                }
                                return (
                                    <div
                                        key={product?.productID || `product-${index}`}
                                        className="col-span-1"
                                    >
                                        <ProductPreview productData={product} />
                                    </div>
                                );
                            })}

                            {columns - currentPageProducts.length > 0 ? (
                                <div
                                    className={`flex h-full w-full items-center justify-center col-span-${columns - currentPageProducts.length}`}
                                    key={`placeholder`}
                                >
                                    <Button
                                        className={`${columns - currentPageProducts.length === 1 ? "w-full" : "w-1/2"}`}
                                        variant={"outline"}
                                        onClick={() => router.push("/shop")}
                                    >
                                        View More
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        className="ml-4 h-8 w-8 flex-shrink-0 border-gray-400"
                        onClick={goToNextPage}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>

                {totalPages > 1 && (
                    <div className="mt-2 flex items-center justify-center space-x-2">
                        <div className="flex space-x-1">
                            {Array.from({ length: totalPages }, (_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPageOverride(index)}
                                    className={`h-2 w-2 rounded-full transition-colors ${
                                        index === currentPage
                                            ? "bg-blue-600"
                                            : "bg-muted-foreground/20 hover:bg-muted-foreground/40"
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </GTMItemListTracker>
    );
}
